import { createSelector } from 'reselect';
import { RootState } from '@store/index';

const getPending = (state: RootState) => state.orders.pending;

const getOrders = (state: RootState) => state.orders.data;

const getError = (state: RootState) => state.orders.error;

export const getOrdersSelector = createSelector(getOrders, (orders) => orders);

export const getOrderHistorySelector = createSelector(getOrders, (orderHistory) => orderHistory);

export const getPendingSelector = createSelector(getPending, (pending) => pending);

export const getErrorSelector = createSelector(getError, (error) => error);

const FALLBACK_DAYS = 7;

const isOrderConsideredDone = (order: IOrder): boolean => {
  const tracking = order.tracking;

  // 1. Check tracking state from Shopify (most reliable)
  if (tracking?.state === 'delivered') return true;

  // 2. Check if a delivered event exists (regardless of date)
  const deliveredEvent = tracking?.events?.find((event: TrackingEvent) => event.id === 'delivered');
  if (deliveredEvent?.updatedAt) return true;

  // 3. If active tracking info is present (carrier, non-delivered state, or
  // post-`created` events), the order is still in progress. Trust tracking and
  // do NOT fall back to the billing-date heuristic, otherwise orders that ship
  // late (where billingDate is older than the fulfillment) would wrongly be
  // moved to past orders while they're still in transit.
  const hasActiveTracking =
    Boolean(tracking?.state) ||
    Boolean(tracking?.carrier?.number) ||
    Boolean(
      tracking?.events?.some(
        (event: TrackingEvent) => event.id && event.id !== 'created' && event.updatedAt,
      ),
    );
  if (hasActiveTracking) return false;

  // 4. Fallback: if billing date is older than FALLBACK_DAYS and no tracking
  // info exists, assume order is done.
  if (order.billingDate) {
    const billingDate = new Date(order.billingDate);
    const now = new Date();
    const diffMs = now.getTime() - billingDate.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    if (diffDays > FALLBACK_DAYS) return true;
  }

  return false;
};

export const getLastOrderSelector = createSelector(getOrdersSelector, (orders) => {
  const lastOrder = orders?.[0];
  if (!lastOrder) return null;

  // Must be a real, paid, processed order. Tracking may legitimately be missing
  // right after the order is triggered (Shopify hasn't created the fulfillment
  // yet), so we don't gate on tracking here. `isOrderConsideredDone` below
  // handles the no-tracking case via the FALLBACK_DAYS branch.
  if (lastOrder.status !== 'PROCESSED' || lastOrder.financialStatus !== 'paid') return null;

  // If the order is considered done, don't keep it as current
  if (isOrderConsideredDone(lastOrder)) return null;

  return lastOrder;
});

export const getPastOrdersSelector = createSelector(
  [getOrdersSelector, getLastOrderSelector],
  (orders, lastOrder) => {
    if (!lastOrder) return orders;
    else return orders.slice(1);
  },
);
